import moment from 'moment';
import {PosologySchedule} from '../../backend/enums/posology-schedule';
import {ActivityType} from '../../backend/interfaces/activity-type';
import {translations} from '../../i18n/translations';
import {
  ActivitiesGroupSchedule,
  ActivityTypeSchedule,
  Frequency,
  FrequencyType,
  PeriodsOfTheDaySchedule,
  RepetitionSchedule,
  SosSchedule,
  TimeListSchedule,
  WeekDay,
} from '../../types';
import {getDaysOfTheWeek} from './days-of-the-week';
import {sortTimesListItems} from './sort-times-list-items';
import {titleCase} from './string';

interface DecomposedPosologySchedule {
  readableFrequency: string;
  readableQuantities: string[];
  readableEndDateTime: string;
}

const parseScheduleFrequency = (
  t: any,
  frequency: FrequencyType,
  frequencyValue?: number,
  days?: WeekDay[]
) => {
  let expression: string = t(translations.schedule_field.readable_periodicity.at_every);
  let value = Number(frequencyValue);

  switch (frequency) {
    case Frequency.Hourly:
    case Frequency.Daily:
    case Frequency.Monthly:
      return `${expression} ${value} ${t(translations.schedule_field.readable_periodicity[frequency])}${
        value > 1 ? 's' : ''
      }`;
    case Frequency.Weekly:
      const weekDays = getDaysOfTheWeek();

      const selectedWeekDays = days
        ?.map((weekday: WeekDay) => {
          const matchedDay = weekDays.find(
            (day) => day.key.toLowerCase() === weekday.toLowerCase()
          );
          return matchedDay ? matchedDay.label.toLowerCase() : '';
        })
        .join(', ')
        .replace(/,(?=[^,]*$)/, ` ${t(translations.schedule_field.readable_periodicity.and)}`);

      expression = `${expression} ${value} ${t(translations.schedule_field.readable_periodicity.weekly)}${
        value > 1 ? 's' : ''
      }`;

      if (selectedWeekDays) {
        expression += `, ${t(translations.schedule_field.readable_periodicity.weekly_at)} ${selectedWeekDays}`;
      }

      return expression;
    default:
      return t(translations.schedule_field.readable_periodicity.no_repetition);
  }
};

const posologySchedulePrefix = ({
  t,
  scheduleDefinition,
}: {
  t: any;
  scheduleDefinition: TimeListSchedule | PeriodsOfTheDaySchedule | RepetitionSchedule | SosSchedule;
}) => {
  switch (scheduleDefinition.posologyScheduleType) {
    case PosologySchedule.PeriodsOfTheDay:
      return t(
        translations.activity_type_posology_schedule_form.fields.posology_schedule_type.options[
          PosologySchedule.PeriodsOfTheDay
        ]
      );
    case PosologySchedule.TimesList:
      return scheduleDefinition.timesList
        .sort(sortTimesListItems)
        .map((item) => moment(item.time).format('HH:mm') + ` - ${item.quantity}`)
        .join(', ')
        .replace(/,(?=[^,]*$)/, ` ${t(translations.schedule_field.readable_periodicity.and)}`);
    case PosologySchedule.SOS:
      return t(
        translations.activity_type_posology_schedule_form.fields.posology_schedule_type.options[
          PosologySchedule.SOS
        ]
      );
    case PosologySchedule.Repetition:
      const qtyLabel = t(translations.schedule_field.quantities);
      const qtySingleLabel = t(
        translations.activity_type_posology_schedule_form.fields.time_list.quantity.label
      );
      return `${scheduleDefinition.repetitionQuantity} ${scheduleDefinition.repetitionQuantity > 1 ? qtyLabel : qtySingleLabel}`;
    default:
      return t(translations.schedule_field.readable_periodicity.not_defined);
  }
};

export const scheduleReadableExpression = ({
  t,
  scheduleDefinition,
  hidePrefix,
}: {
  t: any;
  scheduleDefinition:
    | ActivitiesGroupSchedule
    | ActivityTypeSchedule
    | TimeListSchedule
    | PeriodsOfTheDaySchedule
    | RepetitionSchedule
    | SosSchedule;
  hidePrefix?: boolean;
}) => {
  // Posology schedule expression, parse its prefix
  let expressionPrefix: string = '';
  if (typeof scheduleDefinition !== 'object' || scheduleDefinition === null) {
    console.error('Schedule is not an object.', scheduleDefinition);
    return;
  }

  if ('posologyScheduleType' in scheduleDefinition) {
    expressionPrefix = posologySchedulePrefix({t, scheduleDefinition});

    if (scheduleDefinition.posologyScheduleType === PosologySchedule.SOS) {
      return `${expressionPrefix}`.trim();
    }
  }

  const {frequency, frequencyValue, days} = scheduleDefinition;

  // Activities group schedule expression
  if ('type' in scheduleDefinition) {
    const {type} = scheduleDefinition;
    let expression: string = t(translations.schedule_field.readable_periodicity.at_every);
    let value = Number(frequencyValue);

    if (type === 'activities-group') {
      expression = `${t(
        translations.periodicity_form.fields.frequency_value.activity
      )}, ${expression.toLowerCase()} ${value} ${t(translations.schedule_field.readable_periodicity[frequency])}${
        value > 1 ? 's' : ''
      }`;

      if (frequency === 'weekly') {
        const weekDays = getDaysOfTheWeek();

        const selectedWeekDays = days!
          .map((weekday) => {
            const matchedDay = weekDays.find(
              (day) => day.key.toLowerCase() === weekday.toLowerCase()
            );
            return matchedDay ? matchedDay.label.toLowerCase() : '';
          })
          .join(', ')
          .replace(/,(?=[^,]*$)/, ` ${t(translations.schedule_field.readable_periodicity.and)}`);

        expression += `, ${selectedWeekDays}`;
      }

      return expression;
    } else {
      return t(translations.periodicity_form.fields.frequency_value.task);
    }
  } else {
    const expression = parseScheduleFrequency(t, frequency, frequencyValue, days);

    // If any prefix was added (e.g., from a posology schedule) concatenate it to the main expression and
    // titleCase() the entire string. Also trims the string first, in case a prefix wasn't given.
    if (hidePrefix) {
      return titleCase(`${expression}`.trim());
    }
    return titleCase(`${expressionPrefix} ${expression}`.trim());
  }
};

export const decomposedPosologyScheduleExpression = ({
  t,
  scheduleDefinition,
  periodsOfTheDayMap,
}: {
  t: any;
  scheduleDefinition: TimeListSchedule | PeriodsOfTheDaySchedule | RepetitionSchedule | SosSchedule;
  periodsOfTheDayMap?: {[key: string]: ActivityType};
}): DecomposedPosologySchedule => {
  const {frequency, frequencyValue, days} = scheduleDefinition;
  let expressionPrefix: string = '';
  let readableQuantities: string[] = [];

  switch (scheduleDefinition.posologyScheduleType) {
    case PosologySchedule.PeriodsOfTheDay:
      // Set frequency prefix
      expressionPrefix = t(
        translations.activity_type_posology_schedule_form.fields.posology_schedule_type.options[
          PosologySchedule.PeriodsOfTheDay
        ]
      );

      // Quantities
      if (periodsOfTheDayMap) {
        readableQuantities = scheduleDefinition.periodsOfTheDay.map(
          (periodOfDay) =>
            `${periodsOfTheDayMap?.[periodOfDay.activityTypeId]?.name} - ${periodOfDay.quantity}`
        );
      }

      break;
    case PosologySchedule.TimesList:
      // Quantities
      readableQuantities = scheduleDefinition.timesList
        .sort(sortTimesListItems)
        .map((timeEntry) => `${moment(timeEntry.time).format('HH:mm')} - ${timeEntry.quantity}`);
      break;
    case PosologySchedule.Repetition:
      // Quantities
      readableQuantities = [
        `${t(translations.schedule_field.quantities)} - ${scheduleDefinition.repetitionQuantity}`,
      ];
      break;
    case PosologySchedule.SOS:
      // Quantities
      readableQuantities = [
        `${t(
          translations.activity_type_posology_schedule_form.fields.posology_schedule_type.options[
            PosologySchedule.SOS
          ]
        )}`,
      ];
      break;
  }

  // Frequency part
  const expression = parseScheduleFrequency(t, frequency, frequencyValue, days);
  const readableFrequency = titleCase(`${expressionPrefix} ${expression}`.trim());

  // End date and time
  let readableEndDateTime = titleCase(
    `${t(translations.schedule_field.readable_periodicity.until_end_day)} ${moment(
      scheduleDefinition.endDate
    ).format('DD/MM/YYYY')}`
  );

  if (scheduleDefinition.endHour) {
    readableEndDateTime += ` ${t(translations.schedule_field.readable_periodicity.at_hour)} ${moment(
      scheduleDefinition.endHour
    ).format('HH:mm')}`;
  }

  return {
    readableFrequency,
    readableQuantities,
    readableEndDateTime,
  };
};
